import { forwardRef, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { Field, Input, NativeSelect, Textarea, cn, type InputProps, type NativeSelectProps } from '@elbklar/ui';

/*
 * Felder der Finanzen: Field mit Input, Textarea oder NativeSelect aus
 * @elbklar/ui, Label, Hinweis und Fehler in einem Baustein. Dieselbe dünne
 * Zusammensetzung wie `components/form/Felder.tsx` in crm-web; eigene Optik
 * gibt es hier nicht.
 */

interface Beschriftung {
  label: ReactNode;
  /** Hinweis unter dem Feld. */
  hint?: ReactNode;
  /** Fehlertext; setzt aria-invalid am Feld. */
  error?: ReactNode;
  /** Klassen für den Block aus Label, Feld und Hinweis. */
  className?: string;
}

export type TextFieldProps = Omit<InputProps, 'className'> & Beschriftung;

/** Textfeld mit Label, Hinweis und Fehler. */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, className, required, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={cn('w-full', className)}>
      {(f) => <Input ref={ref} required={required} {...f} {...rest} />}
    </Field>
  );
});

export type TextareaFieldProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & Beschriftung;

/** Mehrzeiliges Feld mit Label, Hinweis und Fehler. */
export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(function TextareaField(
  { label, hint, error, className, required, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={cn('w-full', className)}>
      {(f) => <Textarea ref={ref} required={required} {...f} {...rest} />}
    </Field>
  );
});

export type SelectFieldProps = Omit<NativeSelectProps, 'className'> & Beschriftung;

/** Auswahl (natives <select>, am Handy die Auswahl des Systems) mit Label und Hinweis. */
export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, hint, error, className, required, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={cn('w-full', className)}>
      {(f) => <NativeSelect ref={ref} required={required} {...f} {...rest} />}
    </Field>
  );
});
